import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEFAULT_SETTINGS, type SessionView } from '@cercle/shared';
import { useApi } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { errorMessage } from '@/shared/http/http-client';
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Field,
  Input,
  Kicker,
  Muted,
  Skeleton,
  Status,
} from '@/shared/ui';
import { formatSessionDate, formatSessionTimeRange } from '../session/format';
import { endFromStart, toIsoFromLocalInput } from './session-form';
import styles from './admin.module.scss';

/**
 * Régie — le calendrier des séances.
 *
 * Le cron pose la séance du mois tout seul (US-11) ; cet écran sert aux exceptions —
 * une séance en plus, une date déplacée, une saisie à annuler. C'est aussi le seul endroit
 * d'où l'on atteint une séance passée pour la relire.
 */
export function AdminSessionsListScreen(): ReactNode {
  const api = useApi();
  const sessions = useAsync(() => api.sessions.list(), []);
  // Les réglages ne servent qu'à préremplir la fin : tant qu'ils n'arrivent pas, la durée par
  // défaut du cercle fait l'affaire plutôt que de bloquer le formulaire.
  const settings = useAsync(() => api.settings.read(), []);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className={styles.page}>
      <header>
        <Kicker>Régie</Kicker>
        <h1>Les séances du cercle</h1>
        <Muted>
          La séance mensuelle se pose d’elle-même. On n’en crée une à la main que pour une
          soirée en plus.
        </Muted>
      </header>

      <ErrorText>{error}</ErrorText>
      <CreateSession
        durationMinutes={
          settings.data?.sessionDurationMinutes ?? DEFAULT_SETTINGS.sessionDurationMinutes
        }
        onDone={sessions.reload}
        onError={setError}
      />

      {sessions.loading ? <Skeleton lines={4} /> : null}
      {!sessions.loading && (sessions.data?.length ?? 0) === 0 ? (
        <EmptyState>Aucune séance au calendrier.</EmptyState>
      ) : null}

      <ul className={styles.rows}>
        {[...(sessions.data ?? [])]
          // La plus proche en tête : c'est celle qu'on vient conduire.
          .sort((a, b) => b.startsAt.localeCompare(a.startsAt))
          .map((session: SessionView) => (
            <li key={session.id} className={styles.row}>
              <span className={styles.rank}>{session.number}</span>
              <span className={styles.rowInfo}>
                <strong>{formatSessionDate(session.startsAt)}</strong>
                <Muted>
                  {formatSessionTimeRange(session.startsAt, session.endsAt)}
                  {session.location ? ` · ${session.location}` : ''} · {session.rums.length} rhum
                  {session.rums.length > 1 ? 's' : ''}
                </Muted>
              </span>
              <Status kind={STATUS_KIND[session.status]}>{STATUS_LABEL[session.status]}</Status>
              <Link className={styles.rowLink} to={`/regie/seance/${session.id}`}>
                Conduire
              </Link>
            </li>
          ))}
      </ul>
    </div>
  );
}

const STATUS_LABEL: Record<SessionView['status'], string> = {
  PLANNED: 'Planifiée',
  OPEN: 'En cours',
  CLOSED: 'Clôturée',
};

/** Le sauge ne sert qu'à confirmer : « en cours » est le seul état qui l'appelle. */
const STATUS_KIND: Record<SessionView['status'], 'active' | 'disabled' | 'ephemeral'> = {
  PLANNED: 'ephemeral',
  OPEN: 'active',
  CLOSED: 'disabled',
};

interface CreateProps {
  /** Durée par défaut d'une séance, pour préremplir la fin. */
  durationMinutes: number;
  onDone: () => void;
  onError: (message: string | null) => void;
}

function CreateSession({ durationMinutes, onDone, onError }: CreateProps): ReactNode {
  const api = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [location, setLocation] = useState('');
  // Déplacer le début redéplace la fin — jusqu'à ce que la régie la fixe elle-même : à partir
  // de là, c'est sa valeur qui commande, on ne la lui reprend pas.
  const [endEditedByHand, setEndEditedByHand] = useState(false);

  function changeStart(value: string): void {
    setStartsAt(value);
    if (!endEditedByHand) setEndsAt(endFromStart(value, durationMinutes));
  }

  if (!open) {
    return (
      <Button variant="dashed" block onClick={() => setOpen(true)}>
        + Programmer une séance
      </Button>
    );
  }

  async function create(): Promise<void> {
    if (!startsAt) {
      onError('Il faut au moins une date de début.');
      return;
    }
    if (endsAt && new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      onError('La fin doit venir après le début.');
      return;
    }
    setBusy(true);
    onError(null);
    try {
      // Le lieu vide part en `null` et non en chaîne vide : l'API retombe alors sur le
      // lieu par défaut du cercle plutôt que d'enregistrer une séance sans adresse.
      const created = await api.admin.createSession({
        startsAt: toIsoFromLocalInput(startsAt),
        endsAt: endsAt ? toIsoFromLocalInput(endsAt) : null,
        location: location.trim() || null,
      });
      setOpen(false);
      onDone();
      void navigate(`/regie/seance/${created.id}`);
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={styles.picker}>
      <h3 className={styles.sectionTitle}>Programmer une séance</h3>
      <Field label="Début *">
        <Input
          type="datetime-local"
          value={startsAt}
          onChange={(event) => changeStart(event.target.value)}
        />
      </Field>
      <Field label="Fin" hint="Vide : la fin sera fixée à la clôture.">
        <Input
          type="datetime-local"
          value={endsAt}
          min={startsAt || undefined}
          onChange={(event) => {
            setEndEditedByHand(true);
            setEndsAt(event.target.value);
          }}
        />
      </Field>
      <Field label="Lieu" hint="Vide : le lieu habituel du cercle.">
        <Input
          value={location}
          onChange={(event) => setLocation(event.target.value)}
          placeholder="Salle Courbet"
        />
      </Field>
      <div className={styles.adminActions}>
        <Button disabled={busy} onClick={() => void create()}>
          {busy ? 'Création…' : 'Programmer'}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Annuler
        </Button>
      </div>
    </Card>
  );
}
